import { FollowUpActionStatus, GoalStatus, Carer } from '@lmc/ts-common'
import {
  Avatar,
  Button,
  Handler,
  Input,
  InputSelect,
  ReorderableItem,
  ReorderableItemProps,
  Trash,
} from '@lmc/ui-kit'
import Text from '@lmc/ui-kit/src/general/Typography/Text/Text'
import { Form } from 'antd'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { hasActionPlanTitleError } from '../../utils'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useGroupedCarersWithSelected } from 'hooks/useGroupedOptions'

export type ActionPlanStatus = GoalStatus | FollowUpActionStatus

export type ActionPlanItemValue<T extends ActionPlanStatus> = {
  /**
   * this is the id used for ReorderableList
   * this is not the DB ID
   */
  id: ReorderableItemProps['id']
  title: string
  status: T
  itemStoredID: ReorderableItemProps['id'] | undefined
  canDelete?: boolean
  canEdit?: boolean
  assignedUserId?: string | null
}

export type ActionPlanItemStatusOption<T extends ActionPlanStatus> = {
  value: T
  label: string
}

export interface ActionPlanItemProps<T extends ActionPlanStatus>
  extends ReorderableItemProps,
    ActionPlanItemValue<T> {
  onChange: (value: ActionPlanItemValue<T>) => void
  onRemove: (id: string | number) => void
  disabled?: boolean
  options: ActionPlanItemStatusOption<T>[]
  defaultValue: T
  showOwnerSelection?: boolean
}

export const ACTION_PLAN_ITEM_DELETE_ID = 'ACTION_PLAN_ITEM_DELETE_ID'

const ActionPlanItem = <T extends ActionPlanStatus>(
  props: ActionPlanItemProps<T>,
) => {
  const {
    disabled,
    title,
    onChange,
    id,
    onRemove,
    itemStoredID,
    status,
    options,
    defaultValue,
    canDelete = true,
    canEdit = true,
    showOwnerSelection = false,
    ...reorderableItemSpecificProps
  } = props
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [hasBeenChanged, setHasBeenChanged] = useState(false)

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers,
  )

  const groupedCarerOptions = useGroupedCarersWithSelected({assignedUserId: props.assignedUserId, skip: !showOwnerSelection})

  const titleChangeHandler = (value: string) => {
    const newValue: ActionPlanItemValue<T> = {
      id,
      title: value,
      status,
      itemStoredID,
      canDelete,
      canEdit,
      assignedUserId: props.assignedUserId,
    }

    setHasBeenChanged(true)
    onChange(newValue)
  }

  const statusChangeHandler = (value: T) => {
    const newValue: ActionPlanItemValue<T> = {
      id,
      title,
      status: value,
      itemStoredID,
      canDelete,
      canEdit,
      assignedUserId: props.assignedUserId,
    }

    onChange(newValue)
  }

  const handleOwnerChange = (selectedCarer: string | null) => {
    const newValue: ActionPlanItemValue<T> = {
      id,
      title,
      status,
      itemStoredID,
      canDelete,
      canEdit,
      assignedUserId: selectedCarer,
    }

    onChange(newValue)
  }

  const selectedCarerName =
    showOwnerSelection && props.assignedUserId
      ? carers?.find((carer) => carer.id.toString() === props.assignedUserId)
          ?.name
      : null

  const getDisplayName = () => {
    if (
      showOwnerSelection &&
      selectedCarerName &&
      selectedCarerName.first &&
      selectedCarerName.last
    ) {
      return `${selectedCarerName.first} ${selectedCarerName.last}`
    }
    return null
  }

  const hasError = hasBeenChanged && hasActionPlanTitleError(title)

  const itemValidateStatus = hasError ? 'error' : undefined

  return (
    <ReorderableItem handler id={id} {...reorderableItemSpecificProps}>
      <Wrapper>
        <HandlerStyled id={id} />
        <ContentWrapper>
          <TitleFormWrapper>
            <Form.Item validateStatus={itemValidateStatus}>
              <InputStyled
                placeholder={t(incidentReportsConstants.ENTER_TITLE)}
                disabled={disabled || !canEdit}
                onChange={(e) => titleChangeHandler(e.target.value)}
                value={title}
              />
            </Form.Item>
          </TitleFormWrapper>
          <RightContentWrapper>
            {showOwnerSelection && (
              <OwnerWrapper>
                <OwnerInputSelectStyled
                  placeholder={`${t(incidentReportsConstants.UNASSIGNED)}`}
                  groupedOptions={groupedCarerOptions}
                  onChange={handleOwnerChange}
                  value={props.assignedUserId}
                  showSearch={true}
                  hasClear={true}
                  listHeight={200}
                  virtual={true}
                  disabled={disabled}
                />
              </OwnerWrapper>
            )}
            <InputSelectStyled
              onChange={(value: any) => statusChangeHandler(value)}
              options={options}
              defaultValue={status || defaultValue}
            />
            {canDelete ? (
              <ConfirmationModal>
                <ButtonStyled
                  onClick={() => onRemove(id)}
                  variant="textDanger"
                  danger
                >
                  <DeleteOutlinedStyled
                    data-testid={ACTION_PLAN_ITEM_DELETE_ID}
                  />
                </ButtonStyled>
              </ConfirmationModal>
            ) : (
              <DeleteSpace />
            )}
          </RightContentWrapper>
        </ContentWrapper>
      </Wrapper>
    </ReorderableItem>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.color.neutral.color10};
  border-radius: 15px;
  padding: 16px 24px;
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    padding: 12px 16px;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`

const TitleFormWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 1;
  min-width: 0;

  .ant-form-item {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    width: 100%;

    .ant-form-item {
      width: 100%;
    }
  }
`

const InputStyled = styled(Input)`
  max-width: 596px;
  width: 100%;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`

const OwnerWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;

  > div:first-child {
    color: ${({ theme }) => theme.color.neutral.color3};
    line-height: 1.2;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    white-space: normal;

    > div:first-child {
      margin-bottom: 4px;
    }
  }
`

const OwnerInputSelectStyled = styled(InputSelect as any)`
  width: 220px !important;
  min-width: 220px !important;

  @media (max-width: 768px) {
    width: 100% !important;
    min-width: 100% !important;
  }
`

const RightContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: 18px;
  justify-content: end;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`
const HandlerStyled = styled(Handler)`
  height: 100%;

  @media (max-width: 768px) {
    align-self: center;
    height: auto;
  }
`

const DeleteOutlinedStyled = styled(Trash)`
  cursor: pointer;
  width: 20px;
  height: 20px;
`

const ButtonStyled = styled(Button)`
  color: ${({ theme }) => theme.color.neutral.color5};

  @media (max-width: 768px) {
    align-self: center;
    width: fit-content;
  }
`

const DeleteSpace = styled.div`
  width: 20px;
  height: 20px;

  @media (max-width: 768px) {
    align-self: center;
  }
`

const InputSelectStyled = styled(InputSelect as any)`
  flex: 1;
  max-width: 150px;

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
  }
`

export default ActionPlanItem
